// Slug ↔ bare-string mapping for the transfer domain (batch 011 / ADR-9 ③).
//
// Same arrangement, and the same reasoning, as patient-slug.ts: the backend
// stores and transmits enum values as SLUGS (`#TransferStatus.Pending`), while
// every frontend consumer keys on the legacy bare strings — and all of those
// consumers are typed `any`, so TypeScript cannot see a slug arriving.
//
// What breaks if a slug reaches a renderer:
//   · TransferPatientStatusColor indexes by the bare value to colour the inbox
//     status pill — an unmatched key leaves every pill with no colour;
//   · the pill's label is `cdb_patient_status_<value>_text`, so an unmatched
//     value renders the literal i18n key to the user;
//   · TransferStatusText / TransferStatusIcon index by the bare value in the
//     transfer-log modal — an unmatched key renders a missing icon and the raw
//     key as the timeline entry's heading;
//   · the reject-reason line reads `cdb_<option>_text`.
//
// ⚠️ EXPLICIT TABLES, NOT DERIVATION. Stripping the prefix and un-camel-casing
// would be right for most values and wrong for the ones that matter:
// `OutsideOfVisitationCriteria` has to land on `outside_of_visitation_criteria`
// and `CancelledByReferrer` on `cancelled_by_referrer`, and a derivation that
// gets those right by luck of capitalisation is the same half-correct shape
// that has already bitten this repo twice (the language table whose English
// value was accidentally correct, and `Ehospitalet` deriving correctly only
// because of how it happens to be capitalised). A missing table entry fails
// loudly; a wrong derivation fails silently.

// ── #TransferStatus → the legacy PATIENT-status vocabulary the pill renders ──
//
// NOTE the vocabularies are not the same size, and this is not an oversight.
// The ticket has four states; the pill has always shown the patient-facing
// wording, which is what TransferPatientStatusColor is keyed on:
//   Pending   → pending      (awaiting the receiver's decision)
//   Accepted  → ehospitalet  (the patient is now treated by the receiver)
//   Rejected  → rejected
//   Ended     → discharged   … unless the ending reason was death, see below.
const STATUS_SLUG_TO_BARE: Record<string, string> = {
  '#TransferStatus.Pending': 'pending',
  '#TransferStatus.Accepted': 'ehospitalet',
  '#TransferStatus.Rejected': 'rejected',
  '#TransferStatus.Ended': 'discharged',
};

// ── #TransferOption → the legacy reject/end reason keys ──
//
// The first four are the frozen RejectReasonTypes values (constants/patient.ts),
// which the reject dropdown submits and the log modal renders as
// `cdb_<value>_text`. `deceased` is not selectable in that dropdown — it is
// produced by the death chain — but it arrives on the wire and the log modal
// has an icon and a caption for it.
//
// PD-3005 appends five ASSESSMENT CATEGORIES below the old five. They are the
// only options the MVH pending decision submits from now on; the old five stay
// because the Ended path still uses four of them and every pre-release transfer
// log row still renders through all of them (contract §1, §6).
const OPTION_SLUG_TO_BARE: Record<string, string> = {
  '#TransferOption.OutsideOfVisitationCriteria':
    'outside_of_visitation_criteria',
  '#TransferOption.Capacity': 'capacity',
  '#TransferOption.CancelledByReferrer': 'cancelled_by_referrer',
  '#TransferOption.Other': 'other',
  '#TransferOption.Deceased': 'deceased',
  '#TransferOption.PrescriptionWithFollowUp': 'prescription_with_follow_up',
  '#TransferOption.PrescriptionNoFollowUp': 'prescription_no_follow_up',
  '#TransferOption.Advice': 'advice',
  '#TransferOption.PhysicalAdmission': 'physical_admission',
  '#TransferOption.NotRelevant': 'not_relevant',
};

// ── #TransferDirection ──
const DIRECTION_SLUG_TO_BARE: Record<string, string> = {
  '#TransferDirection.Outgoing': 'outgoing',
  '#TransferDirection.Incoming': 'incoming',
};

// ── #TransferEvent → the legacy log vocabulary ──
//
// The log modal splits on two things: `log_type` ('share' for consent entries,
// 'log' for everything else) and `status`, which indexes TransferStatusText,
// TransferStatusIcon and ConsentStatusText. Six event kinds collapse onto that
// pair, so the table below is the KIND and `eventLogTypeFromKind` derives the
// other half from the same key — one lookup, no chance of the two disagreeing.
//
// Consent entries reuse the accept/reject words because ConsentStatusText is
// keyed that way ('accept' → "accepted data sharing", 'reject' → "withdrew").
const EVENT_KIND_SLUG_TO_BARE: Record<string, string> = {
  '#TransferEvent.Applied': 'pending',
  '#TransferEvent.Accepted': 'accept',
  '#TransferEvent.Rejected': 'reject',
  '#TransferEvent.Ended': 'end',
  '#TransferEvent.ConsentGranted': 'accept',
  '#TransferEvent.ConsentRevoked': 'reject',
};

const CONSENT_EVENT_KINDS = [
  '#TransferEvent.ConsentGranted',
  '#TransferEvent.ConsentRevoked',
];

// Unmatched values are logged and passed through UNCHANGED rather than thrown.
// The enum is closed and the write path rejects foreign values, so a miss means
// the table has drifted from the backend — but one unknown status must not blank
// the whole inbox. Same policy as patient-slug.ts.
const translate = (
  table: Record<string, string>,
  value: string | undefined,
  what: string
): string | undefined => {
  if (value === undefined || value === '') {
    return value;
  }
  const bare = table[value];
  if (bare === undefined) {
    console.error(`unmapped transfer ${what}: ${value}`);
    return value;
  }
  return bare;
};

export const transferStatusSlugToBare = (value: string | undefined) =>
  translate(STATUS_SLUG_TO_BARE, value, 'status');

export const transferOptionSlugToBare = (value: string | undefined) =>
  translate(OPTION_SLUG_TO_BARE, value, 'option');

export const transferDirectionSlugToBare = (value: string | undefined) =>
  translate(DIRECTION_SLUG_TO_BARE, value, 'direction');

export const transferEventKindSlugToBare = (value: string | undefined) =>
  translate(EVENT_KIND_SLUG_TO_BARE, value, 'event kind');

// 'share' | 'log' — the log modal's other axis, derived from the same slug so
// the pair can never disagree. Compared against LogoType.share by the renderer.
export const eventLogTypeFromKind = (kind: string | undefined): string =>
  kind !== undefined && CONSENT_EVENT_KINDS.includes(kind) ? 'share' : 'log';

// ── the inbox row's alarm band lives in patient-slug.ts, not here ──
//
// SETTLED 2026-08-07 at the batch gate, so that nobody re-derives it: the
// backend sends `latest_alarm` as the `#Severity.*` slug verbatim
// (transfer.proto:141) and this client maps it, per ADR-9 F9-4. The gate
// observed the backend sending the bare word `RED` instead; that is a backend
// deviation from the frozen contract and is being fixed there.
//
// There is deliberately NO transfer-specific reader for it. The severity
// vocabulary already has exactly one table in this client — `severitySlugToBare`
// in patient-slug.ts, which batch 010's patient list crosses through — and
// transfer.api.ts calls that same function at its transform. A second name for
// the same mapping is how two spellings of one semantic start to drift apart,
// and a local wrapper would have to be handed the real table to be tested at
// all, which is a test that can never see the table it claims to cover.
//
// What must NOT be added back: a reader that also accepts the bare word. The
// gate's value would have passed straight through it, and the bare severity
// words on this side are lowercase — so `RED` matches nothing in the alarm
// indicator and the whole alarm column stops rendering while every other cell
// in the row looks fine. Tolerating both spellings buys nothing here and costs
// the ability to notice the backend has drifted (patient-slug.ts:69-71).

// ── outbound: bare → slug ──
//
// Requests carry slugs. Built by inverting the tables above so the two
// directions cannot drift apart — safe here because both maps are injective on
// the values these callers submit. Deliberately NOT inverting the event table,
// which is many-to-one and has no outbound caller.
const BARE_TO_STATUS_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(STATUS_SLUG_TO_BARE).map(([slug, bare]) => [bare, slug])
);
const BARE_TO_OPTION_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(OPTION_SLUG_TO_BARE).map(([slug, bare]) => [bare, slug])
);
const BARE_TO_DIRECTION_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(DIRECTION_SLUG_TO_BARE).map(([slug, bare]) => [bare, slug])
);

export const transferStatusBareToSlug = (value: string | undefined) =>
  translate(BARE_TO_STATUS_SLUG, value, 'status');

export const transferOptionBareToSlug = (value: string | undefined) =>
  translate(BARE_TO_OPTION_SLUG, value, 'option');

export const transferDirectionBareToSlug = (value: string | undefined) =>
  translate(BARE_TO_DIRECTION_SLUG, value, 'direction');

// The decision verbs the ticket accepts. `accept` and `reject` are what the
// pending-detail page's two buttons mean; `end` is the discharge path.
//
// ⚠️ These are NOT the pill vocabulary above. `accept` the DECISION maps to
// `#TransferStatus.Accepted`, whose pill reads `ehospitalet`. Keeping a separate
// table for the outbound verb is what stops "ehospitalet" — a patient-facing
// word — from ever being sent as a decision.
const DECISION_BARE_TO_SLUG: Record<string, string> = {
  accept: '#TransferStatus.Accepted',
  reject: '#TransferStatus.Rejected',
  end: '#TransferStatus.Ended',
};

export const transferDecisionBareToSlug = (value: string | undefined) =>
  translate(DECISION_BARE_TO_SLUG, value, 'decision');

// The inbox pill's `deceased` form, restored from the pair the row carries
// (ADR-9 ③). A ticket that ENDED because the patient died shows the legacy
// `deceased` pill rather than `discharged`; every other ending shows
// `discharged`. The ticket status alone cannot express this — which is why the
// list response carries `last_option` next to `status`.
//
// Takes BARE values: it runs after the two tables above, not instead of them.
export const transferPillStatus = (
  bareStatus: string | undefined,
  bareLastOption: string | undefined
): string | undefined =>
  bareStatus === 'discharged' && bareLastOption === 'deceased'
    ? 'deceased'
    : bareStatus;
